import { Component, Input, Output, OnInit, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';

export const CRON_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CronEditorComponent),
  multi: true,
};

interface CronEditorChangeTouchDelegate {
  (val?: string): void;
}

class State {
  minutes: {
    minutes: number,
    seconds: number
  };

  hourly: {
    hours: number,
    minutes: number,
    seconds: number
  };

  daily: {
    days: number,
    hours: number,
    minutes: number,
    seconds: number,
    hourType: string
  };

  weekly: {
    MON: boolean,
    TUE: boolean,
    WED: boolean,
    THU: boolean,
    FRI: boolean,
    SAT: boolean,
    SUN: boolean,
    hours: number,
    minutes: number,
    seconds: number,
    hourType: string
  };

  advanced: {
    expression: string
  };
}

@Component({
  //eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cron-editor',
  templateUrl: './cron-editor.component.html',
  styleUrls: ['./cron-editor.component.scss'],
  providers: [CRON_VALUE_ACCESSOR],
  standalone: false
})
export class CronEditorComponent implements OnInit, ControlValueAccessor {

  @Input() public color: ThemePalette;
  @Input() public disabled: boolean;
  @Input() public options: CronOptions;

  @Input() get cron(): string {
    return this.localCron;
  }

  set cron(value: string) {
    if (value && value !== this.localCron)
      this.localCron = value;

    this.onChange(this.localCron);
  }

  // the name is an Angular convention, @Input variable name + "Change" suffix
  @Output() cronChange = new EventEmitter();

  private activeTabL: string;
  public get activeTab(): string {
    return this.activeTabL;
  }

  public set activeTab(value: string) {
    this.activeTabL = value;
  }

  public tabIndex = 0;
  public selectOptions = this.getSelectOptions();
  public state: State;

  private localCron = '0/5 * 1/1 * *';
  private isDirty: boolean;

  cronForm: UntypedFormControl;

  minutesForm: UntypedFormGroup;
  hourlyForm: UntypedFormGroup;
  dailyForm: UntypedFormGroup;
  weeklyForm: UntypedFormGroup;
  monthlyForm: UntypedFormGroup;
  yearlyForm: UntypedFormGroup;
  advancedForm: UntypedFormGroup;


  get isCronFlavorQuartz() {
    return this.options.cronFlavor === 'quartz';
  }

  get isCronFlavorStandard() {
    return this.options.cronFlavor === 'standard';
  }

  get yearDefaultChar() {
    return this.options.cronFlavor === 'quartz' ? '*' : '';
  }

  get weekDayDefaultChar() {
    return this.options.cronFlavor === 'quartz' ? '?' : '*';
  }

  get monthDayDefaultChar() {
    return this.options.cronFlavor === 'quartz' ? '?' : '*';
  }


  constructor(private fb: UntypedFormBuilder) {
  }

  /* Update the cron output to that of the selected tab.
   * The cron output value is updated whenever a form is updated. To make it change in response to tab selection, we simply reset
   * the value of the form that goes into focus. */
  public onTabFocus(event) {
    const tabLabel = event.tab.textLabel;
    switch (tabLabel) {
      case 'Minutes':
        this.minutesForm.setValue(this.minutesForm.value);
        break;
      case 'Hourly':
        this.hourlyForm.setValue(this.hourlyForm.value);
        break;
      case 'Daily':
        this.dailyForm.setValue(this.dailyForm.value);
        break;
      case 'Weekly':
        this.weeklyForm.setValue(this.weeklyForm.value);
        break;
      case 'Advanced':
        this.advancedForm.setValue(this.advancedForm.value);
        break;
      default:
        throw (new Error('Invalid tab selected'));
    }
  }

  public async ngOnInit() {
    this.state = this.getDefaultState();

    this.cronForm = new UntypedFormControl(this.localCron);

    this.handleModelChange(this.cron);

    this.minutesForm = this.fb.group(this.state.minutes);
    this.minutesForm.valueChanges.subscribe(value => this.computeMinutesCron(value));

    this.hourlyForm = this.fb.group(this.state.hourly);
    this.hourlyForm.valueChanges.subscribe(value => this.computeHourlyCron(value));

    this.dailyForm = this.fb.group(this.state.daily);
    this.dailyForm.valueChanges.subscribe(value => this.computeDailyCron(value));

    this.weeklyForm = this.fb.group(this.state.weekly);
    this.weeklyForm.valueChanges.subscribe(next => this.computeWeeklyCron(next));

    this.advancedForm = this.fb.group(this.state.advanced);
    this.advancedForm.controls.expression.valueChanges.subscribe(next => this.computeAdvancedExpression(next));
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private computeMinutesCron(state: any) {
    this.cron = `${this.isCronFlavorQuartz ? state.seconds : ''} 0/${state.minutes} * 1/1 * ${this.weekDayDefaultChar} ${this.yearDefaultChar}`.trim();
    this.cronForm.setValue(this.cron);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private computeHourlyCron(state: any) {
    this.cron = `${this.isCronFlavorQuartz ? state.seconds : ''} ${state.minutes} 0/${state.hours} 1/1 * ${this.weekDayDefaultChar} ${this.yearDefaultChar}`.trim();
    this.cronForm.setValue(this.cron);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private computeDailyCron(state: any) {
    this.cron = `${this.isCronFlavorQuartz ? state.seconds : ''} ${state.minutes} ${this.hourToCron(state.hours, state.hourType)} 1/${state.days} * ${this.weekDayDefaultChar} ${this.yearDefaultChar}`.trim();
    this.cronForm.setValue(this.cron);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private computeWeeklyCron(state: any) {
    const days = this.selectOptions.days
      .reduce((acc, day) => state[day] ? acc.concat([day]) : acc, [])
      .join(',');
    this.cron = `${this.isCronFlavorQuartz ? state.seconds : ''} ${state.minutes} ${this.hourToCron(state.hours, state.hourType)} ${this.monthDayDefaultChar} * ${days} ${this.yearDefaultChar}`.trim();
    this.cronForm.setValue(this.cron);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private computeAdvancedExpression(expression: any) {
    this.cron = expression;
    this.cronForm.setValue(this.cron);
  }

  public dayDisplay(day: string): string {
    return Days[day];
  }

  public monthWeekDisplay(monthWeekNumber: string): string {
    return MonthWeeks[monthWeekNumber];
  }

  public monthDisplay(month: number): string {
    return Months[month];
  }

  public monthDayDisplay(month: string): string {
    if (month === 'L') {
      return 'Last Day';
    } else if (month === 'LW') {
      return 'Last Weekday';
    } else if (month === '1W') {
      return 'First Weekday';
    } else {
      return `${month}${this.getOrdinalSuffix(month)}`;
    }
  }

  private getAmPmHour(hour: number): number {
    return this.options.use24HourTime ? hour : (hour + 11) % 12 + 1;
  }

  private getHourType(hour: number): string {
    return this.options.use24HourTime ? undefined : (hour >= 12 ? 'PM' : 'AM');
  }

  private hourToCron(hour: number, hourType: string) {
    if (this.options.use24HourTime) {
      return hour;
    } else {
      return hourType === 'AM' ? (hour === 12 ? 0 : hour) : (hour === 12 ? 12 : hour + 12);
    }
  }

  private handleModelChange(cron: string) {
    if (this.isDirty) {
      this.isDirty = false;
      return;
    } else {
      this.isDirty = false;
    }

    if (!this.cronIsValid(cron)) {
      if (this.isCronFlavorQuartz) {
        throw new Error('Invalid cron expression, there must be 6 or 7 segments');
      }

      if (this.isCronFlavorStandard) {
        throw new Error('Invalid cron expression, there must be 5 segments');
      }
    }

    if (cron.split(' ').length === 5 && this.isCronFlavorStandard) {
      cron = `0 ${cron} *`;
    }

    const [seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = cron.split(" ");

    if (cron.match(/\d+ 0\/\d+ \* 1\/1 \* [?*] \*/)) {
      this.activeTab = 'minutes';

      this.state.minutes.minutes = parseInt(minutes.substring(2), 10);
      this.state.minutes.seconds = parseInt(seconds, 10);
      this.tabIndex = 0;
    } else if (cron.match(/\d+ \d+ 0\/\d+ 1\/1 \* [?*] \*/)) {
      this.activeTab = 'hourly';

      this.state.hourly.hours = parseInt(hours.substring(2), 10);
      this.state.hourly.minutes = parseInt(minutes, 10);
      this.state.hourly.seconds = parseInt(seconds, 10);
      this.tabIndex = 1;
    } else if (cron.match(/\d+ \d+ \d+ 1\/\d+ \* [?*] \*/)) {
      this.activeTab = 'daily';
      this.state.daily.days = parseInt(dayOfMonth.substring(2), 10);
      const parsedHours = parseInt(hours, 10);
      this.state.daily.hours = this.getAmPmHour(parsedHours);
      this.state.daily.hourType = this.getHourType(parsedHours);
      this.state.daily.minutes = parseInt(minutes, 10);
      this.state.daily.seconds = parseInt(seconds, 10);
      this.tabIndex = 2;
    } else if (cron.match(/\d+ \d+ \d+ [?*] \* (MON|TUE|WED|THU|FRI|SAT|SUN)(,(MON|TUE|WED|THU|FRI|SAT|SUN))* \*/)) {
      this.activeTab = 'weekly';
      this.selectOptions.days.forEach(weekDay => this.state.weekly[weekDay] = false);
      dayOfWeek.split(',').forEach(weekDay => this.state.weekly[weekDay] = true);
      const parsedHours = parseInt(hours, 10);
      this.state.weekly.hours = this.getAmPmHour(parsedHours);
      this.state.weekly.hourType = this.getHourType(parsedHours);
      this.state.weekly.minutes = parseInt(minutes, 10);
      this.state.weekly.seconds = parseInt(seconds, 10);
      this.tabIndex = 3;
    } else {
      this.activeTab = 'advanced';
      this.state.advanced.expression = this.cron;
      this.tabIndex = 4;
    }
  }

  private cronIsValid(cron: string): boolean {
    if (cron) {
      const cronParts = cron.split(' ');
      return (this.isCronFlavorQuartz && (cronParts.length === 6
        || cronParts.length === 7)
        || (this.isCronFlavorStandard && cronParts.length === 5));
    }

    return false;
  }


  private getDefaultState(): State {
    const [defaultHours, defaultMinutes, defaultSeconds] = this.options.defaultTime.split(':').map(Number);

    return {
      minutes: {
        minutes: 1,
        seconds: 0
      },
      hourly: {
        hours: 1,
        minutes: 0,
        seconds: 0
      },
      daily: {
        days: 1,
        hours: this.getAmPmHour(defaultHours),
        minutes: defaultMinutes,
        seconds: defaultSeconds,
        hourType: this.getHourType(defaultHours)
      },
      weekly: {
        MON: true,
        TUE: false,
        WED: false,
        THU: false,
        FRI: false,
        SAT: false,
        SUN: false,
        hours: this.getAmPmHour(defaultHours),
        minutes: defaultMinutes,
        seconds: defaultSeconds,
        hourType: this.getHourType(defaultHours)
      },
      advanced: {
        expression: this.isCronFlavorQuartz ? '0 * * L-2 * ? *' : '0/5 * 1/1 * *'
      }
    };
  }

  private getOrdinalSuffix(value: string) {
    if (value.length > 1) {
      const secondToLastDigit = value.charAt(value.length - 2);
      if (secondToLastDigit === '1') {
        return 'th';
      }
    }

    const lastDigit = value.charAt(value.length - 1);
    switch (lastDigit) {
      case '1':
        return 'st';
      case '2':
        return 'nd';
      case '3':
        return 'rd';
      default:
        return 'th';
    }
  }

  private getSelectOptions() {
    return {
      months: this.getRange(1, 12),
      monthWeeks: ['#1', '#2', '#3', '#4', '#5', 'L'],
      days: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
      minutes: this.getRange(0, 59),
      fullMinutes: this.getRange(0, 59),
      seconds: this.getRange(0, 59),
      hours: this.getRange(1, 23),
      monthDays: this.getRange(1, 31),
      monthDaysWithLasts: ['1W', ...[...this.getRange(1, 31).map(String)], 'LW', 'L'],
      monthDaysWithOutLasts: [...[...this.getRange(1, 31).map(String)]],
      hourTypes: ['AM', 'PM']
    };
  }

  private getRange(start: number, end: number): number[] {
    const length = end - start + 1;
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    //eslint-disable-next-line prefer-spread
    return Array.apply(null, Array(length)).map((_, i) => i + start);
  }


  /*
   * ControlValueAccessor
   */
  onChange: CronEditorChangeTouchDelegate = () => void 0;
  onTouched: CronEditorChangeTouchDelegate = () => void 0;

  writeValue(obj: string): void {
    this.cron = obj;
  }

  registerOnChange(fn: CronEditorChangeTouchDelegate): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: CronEditorChangeTouchDelegate): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

}

export interface CronOptions {
  formInputClass: string;
  formSelectClass: string;
  formRadioClass: string;
  formCheckboxClass: string;

  defaultTime: string,

  use24HourTime: boolean;
  hideSeconds: boolean;

  cronFlavor: string;
}

export const Days = {
  'SUN': 'Sunday',
  'MON': 'Monday',
  'TUE': 'Tuesday',
  'WED': 'Wednesday',
  'THU': 'Thursday',
  'FRI': 'Friday',
  'SAT': 'Saturday'
};

export const MonthWeeks = {
  '#1': 'First',
  '#2': 'Second',
  '#3': 'Third',
  '#4': 'Fourth',
  '#5': 'Fifth',
  'L': 'Last'
};

export enum Months {
  January = 1,
  February,
  March,
  April,
  May,
  June,
  July,
  August,
  September,
  October,
  November,
  December
}
